import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { environment } from '@mogenius/environments/environment';
import { GithubAppDto, GithubAuthorizedResponseDto, GithubInstallationDto } from '@mogenius/client-sdk/core-dto';
import { Subscription } from 'rxjs';
import { GitAuthorizedEnum } from '@mogenius/git/enums/git-authorized.enum';
import { GithubService } from '@mogenius/git/services/github/github.service';
import { plainToInstance } from 'class-transformer';
import { isArray } from 'class-validator';
import { map } from 'rxjs/operators';
import { MoUtils } from '@mogenius/client-sdk/utils';
import { NavigationService } from '@mogenius/shared/services/navigation/navigation.service';

import { NgOptimizedImage } from '@angular/common';
import { SpinnerComponent } from '@mogenius/shared/components/spinner/spinner.component';

@Component({
    selector: 'mo-github-authorized-page',
    templateUrl: './github-authorized-page.component.html',
    styleUrls: ['./github-authorized-page.component.scss'],
    imports: [NgOptimizedImage, SpinnerComponent]
})
export class GithubAuthorizedPageComponent implements OnInit, OnDestroy {
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _httpClient = inject(HttpClient);
  private readonly _router = inject(Router);
  private readonly _githubService = inject(GithubService);

  private _subscriptions: Subscription;

  private _fallbackUrl = localStorage.getItem('gitConnectionFallbackUrl') || undefined;

  private _state: GitAuthorizedEnum = GitAuthorizedEnum.LOADING;
  private _data: GithubAuthorizedResponseDto;
  private _message: string;

  ngOnInit(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
    this._subscriptions = new Subscription();
    this._subscriptions.add(
      this._activatedRoute.queryParams.subscribe({
        next: async (params: Params): Promise<void> => {
          this.githubAuthorized(params);
          await this._router.navigate([], {
            queryParams: {
              state: null,
              code: null,
              installation_id: null
            },
            queryParamsHandling: 'merge'
          });
        },
        error: () => {},
        complete: () => {}
      })
    );
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }

  private githubAuthorized(params: { [key: string]: string }): void {
    let state = params?.state;
    if (state && state.endsWith(':workspace')) {
      state = MoUtils.replaceAll(state, ':workspace', '');
      const paramsArr = [`state=${state}`];
      for (const key in params) {
        if (key === 'state') {
          continue;
        }
        paramsArr.push(`${key}=${params[key]}`);
      }
      window.open(
        `${window.location.origin}/workspace/git-integration/github/authorized?${paramsArr.join('&')}`,
        '_self'
      );
      return;
    }

    if (!params?.state || Object.keys(params).length === 0) {
      setTimeout(() => {
        if (this._state === GitAuthorizedEnum.LOADING) {
          this._state = GitAuthorizedEnum.FAILED;
        }
      }, 5000);
      return;
    }
    const url = MoUtils.cleanUpUrl(`${this.serviceUrl}/${environment.githubService.githubAuthorized.endPoint}`);
    this._state = GitAuthorizedEnum.LOADING;

    this._subscriptions.add(
      this._httpClient
        .get<GithubAuthorizedResponseDto>(url, {
          headers: {
            'Content-Type': environment.githubService.githubAuthorized.header.contentType,
            hideLoadingSpinner: '1',
            hideErrorMessage: '1'
          },
          params: params
        })
        .pipe(
          map((response: GithubAuthorizedResponseDto) =>
            plainToInstance(GithubAuthorizedResponseDto, response, { excludeExtraneousValues: true })
          )
        )
        .subscribe({
          next: async (data: GithubAuthorizedResponseDto): Promise<void> => {
            this._state = GitAuthorizedEnum.SUCCESS;
            this._data = {
              ...data,
              app: plainToInstance(GithubAppDto, data.app, { excludeExtraneousValues: true }),
              installations: data.installations
                ? data.installations.map((item) =>
                    plainToInstance(GithubInstallationDto, item, { excludeExtraneousValues: true })
                  )
                : undefined
            };
            if (this._data.type === 'SELECT_INSTALLATION') {
              if (this._data.installations?.length === 0) {
                this.openGithubNewInstallation(this._data.callbackUrl, this._data.organizationId, this._data.projectId);
                return;
              }
              if (
                this._data.installations &&
                isArray(this._data.installations) &&
                this._data.installations?.length > 0
              ) {
                return;
              }
            }

            if (this._data.callbackUrl || this._fallbackUrl) {
              window.open(this._fallbackUrl ?? this._data.callbackUrl, '_self');
            }
          },
          error: (err) => {
            this._state = GitAuthorizedEnum.FAILED;
            if (err.status === HttpStatusCode.Unauthorized) {
              console.log(err);
              this._data = {
                callbackUrl: err?.error?.callbackUrl
              } as any;
              this._message = err?.error?.message;
            }
          },
          complete: () => {}
        })
    );
  }

  private get serviceUrl(): string {
    if (environment.githubService.port) {
      return MoUtils.cleanUpUrl(`${environment.githubService.url}:${environment.githubService.port}/`);
    }
    return MoUtils.cleanUpUrl(`${environment.githubService.url}/`);
  }

  public openGithubNewInstallation(callback?: string, organizationId?: string, projectId?: string): void {
    this._subscriptions.add(
      this._githubService.appPublicLink(callback, organizationId, projectId).subscribe({
        next: (link: string) => {
          window.open(link, '_self');
        },
        error: () => {},
        complete: () => {}
      })
    );
  }

  public selectInstallation(installation: GithubInstallationDto): void {
    this._subscriptions.add(
      this._githubService
        .selectInstallationAsConnection(
          installation.id,
          `${this._data.installationsKey}`,
          this._data.organizationId,
          this._data.projectId
        )
        .subscribe({
          next: () => {
            if (this._data.callbackUrl || this._fallbackUrl) {
              window.open(this._fallbackUrl ?? this._data.callbackUrl, '_self');
            }
          },
          error: (err) => {},
          complete: () => {}
        })
    );
  }

  get state(): GitAuthorizedEnum {
    return this._state;
  }

  get gitAuthorizedEnum(): typeof GitAuthorizedEnum {
    return GitAuthorizedEnum;
  }

  get data(): GithubAuthorizedResponseDto {
    return this._data;
  }

  get message(): string {
    return this._message;
  }

  get fallbackUrl(): string | undefined {
    return this._fallbackUrl;
  }
}
